import React from 'react';
import { OperatorTypeDetails, OperatorTypes } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import PropTypes from 'prop-types';
import BooleanChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BooleanChannel/BooleanChannel';
import ConditionalRender
  from '@avtechsoftware/react-component-library/src/components/common/ConditionalRender/ConditionalRender';
import AnalogComputedChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/AnalogComputedChannel/AnalogComputedChannel';
import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import AltErrorWrapper
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/AltErrorWrapper/AltErrorWrapper';
import { useDispatch, useSelector } from 'react-redux';
import BaseTemperatureChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BaseTemperatureChannel/BaseTemperatureChannel';
import IntegerAsBooleanChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/IntegerAsBooleanChannel/IntegerAsBooleanChannel';
import DigitalParticulateTemperatureFaultChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/DigitalParticulateTemperatureFaultChannel/DigitalParticulateTemperatureFaultChannel';
import ExtremeTemperatureFaultChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/ExtremeTemperatureFaultChannel/ExtremeTemperatureFaultChannel';
import {
  AlertFormSteps,
  selectThresholdOperator,
  selectThresholdValue,
  updateStepData,
} from '../../../../redux/slices/alertFormSlice/alertFormSlice';
import { selectSensorMap } from '../../../../redux/slices/deviceList/deviceListSlice';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';
import { getAnalogComputedUnit } from '../../../../redux/slices/deviceList/deviceListHelperUtils';

function IndependentThresholdInput(props) {
  const {
    channel,
    errorList,
  } = props;

  const dispatch = useDispatch();
  const thresholdValue = useSelector(selectThresholdValue);
  const operator = useSelector(selectThresholdOperator);
  const sensorMap = useSelector(selectSensorMap);
  const sessionInformation = useSelector(selectSessionInformation);
  const operators = channel.channelType.thresholdOperators;

  React.useEffect(() => {
    const updateData = {};

    // Set default operator based on channel type
    if (channel instanceof IntegerAsBooleanChannel
        || channel instanceof ExtremeTemperatureFaultChannel) {
      if (!operator) {
        if (channel instanceof ExtremeTemperatureFaultChannel
            || channel instanceof DigitalParticulateTemperatureFaultChannel) {
          updateData.thresholdOperator = '>';
        } else {
          updateData.thresholdOperator = '<';
        }
      }
      if (thresholdValue === null) {
        if (channel instanceof ExtremeTemperatureFaultChannel
            || channel instanceof DigitalParticulateTemperatureFaultChannel) {
          updateData.thresholdValue = 0;
        } else {
          updateData.thresholdValue = 1;
        }
      }
    } else if (channel instanceof BooleanChannel
        && !(channel instanceof ExtremeTemperatureFaultChannel)) {
      if (!operator) {
        updateData.thresholdOperator = '=';
      }
      if (thresholdValue === null) {
        updateData.thresholdValue = 0;
      }
    } else if (!(channel instanceof BooleanChannel)
        && !(channel instanceof IntegerAsBooleanChannel)) {
      if (!operator && operators.length > 0) {
        const firstOperatorType = operators[0];
        updateData.thresholdOperator = firstOperatorType;
      }
    }

    if (Object.keys(updateData).length > 0) {
      dispatch(updateStepData({
        step: AlertFormSteps.DEFINE_THRESHOLD,
        data: updateData,
      }));
    }
  },
  [channel, sensorMap, sessionInformation, dispatch, operator, thresholdValue,
    operators]);

  // Helper function to convert symbol to OperatorTypes
  const getOperatorTypeFromSymbol = (symbol) => {
    const entry = Object.entries(OperatorTypes).find(
      ([, value]) => {
        const details = OperatorTypeDetails[value];
        return details && details.symbol === symbol;
      },
    );
    return entry ? entry[1] : null;
  };

  // Helper function to convert OperatorTypes ID back to symbol
  const getSymbolFromOperatorType = (operatorType) => {
    const details = OperatorTypeDetails[operatorType];
    return details ? details.symbol : null;
  };

  channel.getUnits({
    scale: sessionInformation.temperatureScale,
    airSpeedScale: sessionInformation.airSpeedScale,
  });

  const getThresholdScale = () => {
    const scale = channel instanceof BaseTemperatureChannel
      ? sessionInformation.temperatureScale
      : sessionInformation.airSpeedScale;
    return channel.getUnits({ scale, airSpeedScale: sessionInformation.airSpeedScale });
  };

  const handleChangeValue = (e) => {
    const numericValue = parseFloat(e.target.value);
    dispatch(updateStepData({
      step: AlertFormSteps.DEFINE_THRESHOLD,
      data: { thresholdValue: Number.isNaN(numericValue) ? null : numericValue },
    }));
  };

  const handleBooleanValueChange = (e) => {
    const { value } = e.target;
    const integerValue = parseInt(value, 10);
    // eslint-disable-next-line max-len
    if (channel instanceof ExtremeTemperatureFaultChannel || channel
      instanceof DigitalParticulateTemperatureFaultChannel) {
      dispatch(updateStepData({
        step: AlertFormSteps.DEFINE_THRESHOLD,
        data: { thresholdOperator: integerValue === 1 ? '<' : '>' },
      }));
    } else {
      dispatch(updateStepData({
        step: AlertFormSteps.DEFINE_THRESHOLD,
        data: { thresholdOperator: '=' },
      }));
    }
    dispatch(updateStepData({
      step: AlertFormSteps.DEFINE_THRESHOLD,
      data: { thresholdValue: integerValue },
    }));
  };

  const handleOperatorChange = (e) => {
    const symbol = e.target.value;
    const operatorType = getOperatorTypeFromSymbol(symbol);
    if (operatorType !== null) {
      dispatch(updateStepData({
        step: AlertFormSteps.DEFINE_THRESHOLD,
        data: { thresholdOperator: operatorType },
      }));
    }
  };

  return (
    <FormErrorHandler errorList={errorList}>
      <div className="d-flex align-items-start">
        <ConditionalRender show={channel instanceof IntegerAsBooleanChannel
          && !(channel instanceof DigitalParticulateTemperatureFaultChannel)}
        >
          <div className="mr-2">
            <AltErrorWrapper fieldId="hresholdValue">
              <select
                className="form-control w-auto"
                onChange={handleBooleanValueChange}
                value={thresholdValue || ''}
                style={{ border: '1px solid #ced4da', borderWidth: '0.5px' }}
              >
                <option value={1}>{channel.falseDisplayValue}</option>
                <option value={0}>{channel.trueDisplayValue}</option>
              </select>
            </AltErrorWrapper>
          </div>
        </ConditionalRender>

        <ConditionalRender show={(channel instanceof BooleanChannel
          && !(channel instanceof ExtremeTemperatureFaultChannel))}
        >
          <div className="mr-2">
            <AltErrorWrapper fieldId="thresholdValue">
              <select
                className="form-control w-auto"
                onChange={handleBooleanValueChange}
                value={thresholdValue || ''}
                style={{ border: '1px solid #ced4da', borderWidth: '0.5px' }}
              >
                <option value={0}>{channel.getDisplayValue(0)}</option>
                <option value={1}>{channel.getDisplayValue(1)}</option>
              </select>
            </AltErrorWrapper>
          </div>
        </ConditionalRender>

        {/* eslint-disable-next-line max-len */}
        <ConditionalRender show={(channel instanceof ExtremeTemperatureFaultChannel || channel instanceof DigitalParticulateTemperatureFaultChannel)}>
          <div>
            <input
              className="form-control"
              value="is"
              style={{
                width: '100px',
                marginRight: '0.5rem',
              }}
              readOnly
            />
          </div>
          <div className="mr-2">
            <AltErrorWrapper fieldId="thresholdValue">
              <select
                className="form-control w-auto"
                onChange={handleBooleanValueChange}
                value={thresholdValue || ''}
                style={{ border: '1px solid #ced4da', borderWidth: '0.5px' }}
              >
                <option value={0}>Fault</option>
                {/* replace open circuit displayValue */}
                <option value={1}>No Fault</option>
              </select>
            </AltErrorWrapper>
          </div>
        </ConditionalRender>

        <ConditionalRender
          show={!(channel instanceof BooleanChannel) && !(channel
            instanceof IntegerAsBooleanChannel)
              && !(channel instanceof ExtremeTemperatureFaultChannel)}
        >
          <div className="d-flex align-items-start">
            <div className="mr-2">
              <select
                className="form-control w-auto"
                onChange={handleOperatorChange}
                value={getSymbolFromOperatorType(operator) || ''}
                style={{
                  border: '1px solid #ced4da',
                  borderWidth: '0.5px',
                  marginRight: '0.5rem',
                }}
              >
                {operators.map((operatorType, index) => {
                  const operatorObj = OperatorTypeDetails[operatorType];
                  return (
                    <option
                      key={index}
                      value={operatorObj.symbol}
                    >
                      {operatorObj.label.replace('is', '')}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="mr-2">
              <AltErrorWrapper fieldId="thresholdValue">
                <input
                  className="form-control"
                  type="number"
                  step="0.01"
                  value={thresholdValue != null ? thresholdValue : ''}
                  onChange={handleChangeValue}
                  onFocus={(e) => {
                    e.target.select();
                  }}
                  style={{
                    width: '100px',
                    marginRight: '0.5rem',
                  }}
                />
              </AltErrorWrapper>
            </div>
            <div
              className="d-flex align-items-center"
              style={{ height: '38px', whiteSpace: 'nowrap' }}
            >
              <span className="ml-1">
                {channel instanceof AnalogComputedChannel
                  ? getAnalogComputedUnit(
                    {
                      macAddress: '',
                      channelGroupTypeId: '',
                      channelGroupIndex: '',
                    },
                    sensorMap,
                  )
                  : getThresholdScale()}
              </span>
            </div>
          </div>
        </ConditionalRender>
      </div>
    </FormErrorHandler>
  );
}

IndependentThresholdInput.propTypes = {
  channel: PropTypes.shape({
    displayName: PropTypes.string,
    channelType: PropTypes.shape({
      thresholdOperators: PropTypes.arrayOf(PropTypes.number),
    }),
    falseDisplayValue: PropTypes.string,
    trueDisplayValue: PropTypes.string,
    getDisplayValue: PropTypes.func,
    getUnits: PropTypes.func,
  }).isRequired,
  errorList: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string,
    message: PropTypes.string,
  })),
};

IndependentThresholdInput.defaultProps = {
  errorList: [],
};

export default IndependentThresholdInput;
